import { useState } from "react";
import { DashboardLayout } from "@/components/Dashboard/DashboardLayout";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/apiClient";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Truck, Plus, Edit, Search, Building2, User, Phone, Mail, ArrowLeft, Package, Receipt
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { SupplierFormDialog, type Supplier } from "@/components/Suppliers/SupplierFormDialog";
import type { AccountingAccount } from "@/types/billing";

export default function Suppliers() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState<Partial<Supplier> | null>(null);

  const { data: suppliers = [], isLoading, error } = useQuery<Supplier[]>({
    queryKey: ["suppliers", search],
    queryFn: async () => {
      let query = api
        .from("billing_customers")
        .select("*")
        .in("entity_type", ["supplier", "both"])
        .order("legal_name");

      if (search) {
        query = query.or([`legal_name.ilike.%${search}%`, `tax_id.ilike.%${search}%`, `trade_name.ilike.%${search}%`]);
      }

      const { data, error } = await query;
      if (error) throw error;
      console.log('[Suppliers] Loaded', data?.length || 0, 'suppliers');
      return (data || []) as Supplier[];
    },
    enabled: !!user,
  });

  const { data: expenseAccounts = [] } = useQuery<AccountingAccount[]>({
    queryKey: ["expense-accounts"],
    queryFn: async () => {
      const { data, error } = await api
        .from("accounting_accounts")
        .select("*")
        .like("code", "6%")
        .eq("is_active", true)
        .eq("is_header", false)
        .order("code");
      if (error) throw error;
      return data || [];
    },
  });

  const handleNew = () => {
    setEditingSupplier(null);
    setDialogOpen(true);
  };

  const handleEdit = (e: React.MouseEvent, supplier: Supplier) => {
    e.stopPropagation();
    setEditingSupplier(supplier);
    setDialogOpen(true);
  };

  const handleRowClick = (supplier: Supplier) => {
    navigate(`/billing/suppliers/${supplier.id}`);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(amount || 0);
  };

  const activeSuppliers = suppliers.filter(s => s.is_active).length;
  const totalBalance = suppliers.reduce((sum, s) => sum + (s.current_balance || 0), 0);

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" asChild>
              <Link to="/billing"><ArrowLeft className="w-4 h-4" /></Link>
            </Button>
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-2">
                <Truck className="w-8 h-8 text-orange-600" />
                Proveedores
              </h1>
              <p className="text-muted-foreground">Gestion de proveedores y compras</p>
            </div>
          </div>
          <Button onClick={handleNew}>
            <Plus className="w-4 h-4 mr-2" />
            Nuevo Proveedor
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <Truck className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{suppliers.length}</p>
                  <p className="text-sm text-muted-foreground">Total proveedores</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Package className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{activeSuppliers}</p>
                  <p className="text-sm text-muted-foreground">Activos</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <Receipt className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{formatCurrency(totalBalance)}</p>
                  <p className="text-sm text-muted-foreground">Saldo pendiente</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar por nombre, NIF o nombre comercial..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Suppliers Table */}
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Proveedor</TableHead>
                  <TableHead>NIF/CIF</TableHead>
                  <TableHead>Contacto</TableHead>
                  <TableHead>Cuenta Gasto</TableHead>
                  <TableHead>Saldo</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8">Cargando...</TableCell>
                  </TableRow>
                ) : suppliers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      {search ? "No se encontraron proveedores" : "No hay proveedores. Crea el primero."}
                    </TableCell>
                  </TableRow>
                ) : (
                  suppliers.map((supplier) => (
                    <TableRow
                      key={supplier.id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => handleRowClick(supplier)}
                    >
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                            {supplier.customer_type === 'company' ? (
                              <Building2 className="w-5 h-5 text-orange-600" />
                            ) : (
                              <User className="w-5 h-5 text-orange-600" />
                            )}
                          </div>
                          <div>
                            <p className="font-medium">{supplier.legal_name}</p>
                            {supplier.trade_name && (
                              <p className="text-xs text-muted-foreground">{supplier.trade_name}</p>
                            )}
                            {supplier.entity_type === 'both' && (
                              <Badge variant="outline" className="text-xs">Cliente/Proveedor</Badge>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="font-mono">{supplier.tax_id || '-'}</TableCell>
                      <TableCell>
                        <div className="text-sm space-y-1">
                          {supplier.email && (
                            <div className="flex items-center gap-1 text-muted-foreground">
                              <Mail className="w-3 h-3" />
                              {supplier.email}
                            </div>
                          )}
                          {supplier.phone && (
                            <div className="flex items-center gap-1 text-muted-foreground">
                              <Phone className="w-3 h-3" />
                              {supplier.phone}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {supplier.default_expense_account_id ? (
                          <span className="font-mono text-sm">
                            {expenseAccounts.find(a => a.id === supplier.default_expense_account_id)?.code || '-'}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className={supplier.current_balance > 0 ? 'text-amber-600 font-medium' : ''}>
                          {formatCurrency(supplier.current_balance)}
                        </span>
                      </TableCell>
                      <TableCell>
                        {supplier.is_blocked ? (
                          <Badge variant="destructive">Bloqueado</Badge>
                        ) : supplier.is_active ? (
                          <Badge className="bg-green-100 text-green-700">Activo</Badge>
                        ) : (
                          <Badge variant="secondary">Inactivo</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" onClick={(e) => handleEdit(e, supplier)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      {/* Supplier Dialog */}
      <SupplierFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        supplier={editingSupplier}
      />
    </DashboardLayout>
  );
}
